export const formatCurrency = (value) => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

export const formatSeatRange = (seats) => {
  const list = (seats || []).map((s) => String(s).trim()).filter(Boolean);
  if (list.length === 0) return "";
  if (!list.every((s) => /^\d+$/.test(s))) return list.join(", ");

  const nums = list.map(Number).sort((a, b) => a - b);
  const ranges = [];
  let start = nums[0];
  let prev = nums[0];
  for (let i = 1; i < nums.length; i++) {
    if (nums[i] === prev + 1) {
      prev = nums[i];
      continue;
    }
    ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
    start = prev = nums[i];
  }
  ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
  return ranges.join(", ");
};

export const buildSeatPills = (items = []) => {
  const sections = new Set();
  const rows = new Set();
  const seats = [];
  items.forEach((i) => {
    if (i.section) sections.add(i.section);
    if (i.row) rows.add(i.row);
    if (i.seat) seats.push(i.seat);
  });
  const pills = [];
  if (sections.size) pills.push(`Section ${[...sections].join(", ")}`);
  if (rows.size) pills.push(`Row ${[...rows].join(", ")}`);
  if (seats.length === 1) pills.push(`Seat ${seats[0]}`);
  else if (seats.length > 1) pills.push(`Seats ${formatSeatRange(seats)}`);
  return pills;
};

const numOrInfinity = (v) => (Number.isNaN(parseInt(v, 10)) ? Infinity : parseInt(v, 10));

export const bySeat = (a, b) => {
  const key = (t) => [
    numOrInfinity(t.section), String(t.section ?? ""),
    numOrInfinity(t.row), String(t.row ?? ""),
    numOrInfinity(t.seat), String(t.seat ?? ""),
  ];
  const ka = key(a);
  const kb = key(b);
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] < kb[i]) return -1;
    if (ka[i] > kb[i]) return 1;
  }
  return 0;
};

export const formatCents = (cents) => {
  if (cents == null) return "";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

export const formatDate = (value, opts = {}) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const defaults = { year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" };
  return d.toLocaleString(undefined, { ...defaults, ...opts });
};

export const formatLongDate = (iso, separator = "—") => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const datePart = d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const timePart = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${datePart} ${separator} ${timePart}`;
  } catch (_e) {
    return iso;
  }
};

export const formatVenueShortLine = (venue) => {
  const v = venue || {};
  const cityState = [v.city, v.state_code].filter(Boolean).join(", ");
  return [v.name, cityState].filter(Boolean).join(", ");
};

export const formatShortDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
  } catch (_e) {
    return iso;
  }
};

export const formatEventDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
};

export const formatDateTime = (timeStr, timeZone) => {
  if (!timeStr) return "—";
  const date = new Date(timeStr);
  const options = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  };
  if (timeZone) options.timeZone = timeZone;
  return date.toLocaleDateString('en-US', options);
};

export const formatPercent = (value) => {
  if (value === null || value === undefined) return "—";
  return `${value}%`;
};

export const formatNumber = (value) => {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString();
};

export const formatRatio = (value) => {
  if (value === null || value === undefined) return "—";
  return `${Number(value).toFixed(2)}x`;
};

export const isoToDate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);

export const dateToIso = (date) => {
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const formatDateOnly = (value) => {
  if (!value) return "—";
  const dateOnly = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = dateOnly ? isoToDate(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const formatPhone = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const formatEIN = (value, country = "US") => {
  if (!value) return "";
  if (country === "CA") {
    const digits = value.replace(/\D/g, "").slice(0, 13);
    if (digits.length <= 9) return digits;
    return `${digits.slice(0, 9)}-${digits.slice(9)}`;
  }
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
};

export const maskEIN = (value, country = "US") => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (country === "CA") {
    if (digits.length < 4) return "•••••••••-••••";
    return `•••••••••-${digits.slice(-4)}`;
  }
  if (digits.length < 4) return "••-•••••••";
  return `••-•••${digits.slice(-4)}`;
};

export const einLabelForCountry = (country) => (country === "CA" ? "Business Number (BN-9)" : "EIN");

export const einPlaceholderForCountry = (country) => (country === "CA" ? "123456789" : "00-0000000");

export const einMaxLengthForCountry = (country) => (country === "CA" ? 14 : 10);
